import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Monitor,
  User,
  Clock,
  Volume2,
  ExternalLink,
  X,
  AlertTriangle,
  CheckCircle2,
  WifiOff,
  History,
  Building,
  Edit3,
} from 'lucide-react';
import { haptics } from '../../lib/haptics';
import { sounds } from '../../lib/sounds';
import { IosActivityRing } from './IosActivityRing';

export interface WorkstationSheetData {
  id: string;
  deviceId: string;
  hostname: string;
  deviceName: string;
  os: string;
  agentVersion: string;
  status: 'ACTIVE' | 'INACTIVE' | 'OFFLINE' | string;
  lastSeenAt: string;
  lastActivityAt: string | null;
  user: {
    id: string;
    fullName: string;
    email: string;
    department: string | null;
  } | null;
  activeInactivity: {
    id: string;
    startedAt: string;
    elapsedSeconds: number;
  } | null;
}

interface IosBottomSheetProps {
  workstation: WorkstationSheetData | null;
  isOpen: boolean;
  onClose: () => void;
  onRingDevice: (deviceId: string, deviceName: string) => Promise<void>;
  onEditDevice?: (workstation: any) => void;
  isRinging: boolean;
  now: number;
}

export function IosBottomSheet({
  workstation,
  isOpen,
  onClose,
  onRingDevice,
  onEditDevice,
  isRinging,
  now,
}: IosBottomSheetProps) {
  useEffect(() => {
    if (isOpen) {
      haptics.impact();
      sounds.playChimeSound();
    }
  }, [isOpen]);

  if (!isOpen || !workstation) return null;

  const isInactive = workstation.status === 'INACTIVE';
  const isActive = workstation.status === 'ACTIVE';
  const isOffline = workstation.status === 'OFFLINE';

  const elapsedSeconds = workstation.activeInactivity
    ? Math.max(0, Math.floor((now - new Date(workstation.activeInactivity.startedAt).getTime()) / 1000))
    : 0;

  const mins = Math.floor(elapsedSeconds / 60);
  const secs = elapsedSeconds % 60;
  const elapsedFormatted = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

  const handleClose = () => {
    sounds.playTapSound();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center select-none" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="ios-sheet-backdrop fixed inset-0 transition-opacity"
        onClick={handleClose}
      />

      {/* Sheet Surface */}
      <div className="ios-sheet-surface relative w-full max-w-lg rounded-t-3xl p-5 pb-safe z-10 animate-ios-enter text-zinc-900 dark:text-white transition-colors">
        {/* Pull Handle Bar */}
        <div className="w-12 h-1.5 rounded-full bg-zinc-300 dark:bg-zinc-600/70 mx-auto mb-4 cursor-pointer" onClick={handleClose} />

        {/* Header Strip */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-2xl flex items-center justify-center border shadow-lg ${
                isInactive
                  ? 'bg-amber-500/15 border-amber-500/40 text-amber-500 shadow-amber-500/10'
                  : isActive
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-500 shadow-emerald-500/10'
                  : 'bg-black/5 dark:bg-zinc-800/80 border-black/5 dark:border-zinc-700/60 text-zinc-500 shadow-black/10'
              }`}
            >
              <Monitor className="w-6 h-6" />
            </div>

            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-zinc-900 dark:text-white tracking-tight">{workstation.hostname}</h2>
                <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-black/5 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-black/5 dark:border-white/10 font-mono font-semibold">
                  {workstation.os}
                </span>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                <span>{workstation.user ? workstation.user.fullName : 'Unassigned Workstation'}</span>
                {workstation.user?.department && (
                  <span className="text-zinc-400 dark:text-zinc-500">• {workstation.user.department}</span>
                )}
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-black/5 dark:bg-zinc-800/80 hover:bg-black/10 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-400 hover:text-black dark:hover:text-white flex items-center justify-center border border-black/5 dark:border-white/10 ios-press"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Telemetry Status Card */}
        <div className="mt-4 p-3.5 rounded-2xl bg-black/[0.04] dark:bg-zinc-900/80 border border-black/[0.05] dark:border-white/[0.08] shadow-inner space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">Real-Time State</span>
            {isActive && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30 island-pulse-active">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Active on Workstation
              </span>
            )}
            {isInactive && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 island-pulse-alert">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                Inactive Alert Active
              </span>
            )}
            {isOffline && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border border-black/5 dark:border-zinc-700">
                <WifiOff className="w-3.5 h-3.5" />
                Offline
              </span>
            )}
          </div>

          {isInactive && workstation.activeInactivity && (
            <div className="pt-2 border-t border-black/5 dark:border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IosActivityRing elapsedSeconds={elapsedSeconds} thresholdSeconds={120} size={32} />
                <div>
                  <div className="text-[11px] text-amber-600 dark:text-amber-400 font-bold uppercase tracking-wider">Unexcused Idle Duration</div>
                  <div className="text-sm font-mono font-bold text-amber-700 dark:text-amber-200 mt-0.5">{elapsedFormatted}</div>
                </div>
              </div>
              <span className="text-[10px] text-amber-600/80 dark:text-amber-400/80 font-mono">120s limit</span>
            </div>
          )}

          <div className="pt-2 border-t border-black/5 dark:border-white/[0.06] grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <span className="text-zinc-500 block">Agent Version</span>
              <span className="text-zinc-700 dark:text-zinc-300 font-mono font-semibold">{workstation.agentVersion || 'v1.0.0-win'}</span>
            </div>
            <div>
              <span className="text-zinc-500 block">Last Contact</span>
              <span className="text-zinc-700 dark:text-zinc-300 font-mono font-semibold">
                {workstation.lastSeenAt ? new Date(workstation.lastSeenAt).toLocaleTimeString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <button
            onClick={() => onRingDevice(workstation.deviceId || workstation.id, workstation.deviceName)}
            disabled={isRinging}
            className="flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs shadow-lg shadow-amber-500/20 ios-press disabled:opacity-50"
          >
            <Volume2 className={`w-4 h-4 ${isRinging ? 'animate-bounce' : ''}`} />
            <span>{isRinging ? 'Sending Ring...' : 'Remote Ring Device'}</span>
          </button>

          {workstation.user ? (
            <Link
              to={`/admin/users/${workstation.user.id}`}
              onClick={handleClose}
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-black/5 hover:bg-black/10 text-zinc-900 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-white font-semibold text-xs border border-black/5 dark:border-white/10 ios-press"
            >
              <User className="w-4 h-4 text-pulse-blue" />
              <span>Employee Profile</span>
            </Link>
          ) : (
            <Link
              to={`/admin/devices`}
              onClick={handleClose}
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-black/5 hover:bg-black/10 text-zinc-900 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-white font-semibold text-xs border border-black/5 dark:border-white/10 ios-press"
            >
              <ExternalLink className="w-4 h-4 text-zinc-500" />
              <span>Manage Device</span>
            </Link>
          )}

          {onEditDevice && (
            <button
              onClick={() => {
                handleClose();
                onEditDevice(workstation);
              }}
              className="col-span-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-2xl bg-pulse-blue/10 hover:bg-pulse-blue/20 text-pulse-blue font-semibold text-xs border border-pulse-blue/30 ios-press"
            >
              <Edit3 className="w-4 h-4" />
              <span>Edit Name & Assign Staff</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
